import cn from "classnames"
import List from "rc-virtual-list"
import { useEffect, useState } from "react"

import { Button, Empty, Flex, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { ExperimentOutlined, LockOutlined } from "@/icons"
import { Form } from "@/organisms/Form"
import { SECTION_IDS } from "@/pages/SettingsPage/components/pages/GuidancePage/helpers"
import { IssueTestImpact } from "./IssueTestImpact"
import { LoaderTestImpact } from "./LoaderTestImpact"
import { StatisticsTestImpact } from "./StatisticsTestImpact"

import styles from "./TestImpactFormSection.module.scss"

import type {
  IssuePreviewSastGuidanceAPI,
  VerdictPreviewSastGuidanceAPI
} from "@/api/useSettings.types"
import type { TestImpactFormSectionProps } from "."

export const TestImpactFormSection = (props: TestImpactFormSectionProps) => {
  const { t: tTestImpact } = useTranslation("translation", {
    keyPrefix: "settings.kai-guidance.form.testImpact"
  })
  const { impactCtx } = props

  const id = SECTION_IDS.guidanceTestImpact
  const name = id
  const footerType = "footer-button"
  const { form, message } = Form.useForm()
  const sectionEnabled = Form.useWatch(name, form)
  const data = impactCtx?.data?.data
  const metadata = impactCtx?.data?.metadata

  const [state, setState] = useState<{
    currentData:
      | null
      | (IssuePreviewSastGuidanceAPI | { type: string; id: string })[]
    summary: {
      sampled: number
      changed: number
    }
  }>({ currentData: null, summary: { sampled: 0, changed: 0 } })
  const { currentData, summary } = state

  useEffect(() => {
    if (sectionEnabled) {
      const el = document
        .getElementById("aside-page-with-breadcrumbs-template")
        ?.querySelector(`a[href="#${id}"]`) as HTMLAnchorElement

      el?.click()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionEnabled])

  const hasNext = metadata?.hasNext

  useEffect(() => {
    if (!sectionEnabled && Array.isArray(currentData))
      setState({ currentData: null, summary: { sampled: 0, changed: 0 } })
    else if (sectionEnabled && data)
      setState((prevData) => {
        const { currentData, summary } = prevData
        const { sampled, changed } = summary
        const { results, summary: newSummary } = data

        const getVerdict = (
          skipReason: string | null,
          verdict: VerdictPreviewSastGuidanceAPI | null
        ) => {
          if (!verdict)
            return {
              isFalsePositive: null,
              llmIssueDescription: skipReason || "",
              falsePositiveReason: "",
              consideredGuidance: false
            }

          return skipReason
            ? {
                ...verdict,
                isFalsePositive: null,
                llmIssueDescription: skipReason
              }
            : verdict
        }

        return {
          summary: {
            sampled: sampled + (newSummary?.sampled ?? 0),
            changed: changed + (newSummary?.changed ?? 0)
          },
          currentData: [
            ...(currentData ?? []).filter((item) => {
              if ("type" in item) return item.type !== footerType

              return true
            }),
            ...results,
            ...(!currentData && !hasNext
              ? []
              : [{ id: footerType, type: footerType }])
          ].map((item, index) => {
            if ("type" in item) return item

            const { after, before, ...rest } = item

            return {
              index,
              id: `${item.issueId}_${item.coordinate.startLine}`,
              before: getVerdict(null, before),
              after: getVerdict(rest.skipReason, after),
              ...rest
            }
          })
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sectionEnabled])

  const currentDataLength = currentData?.length ?? 0

  const itemHeight = currentDataLength < 5 ? 60 : 58
  const listMaxHeight = itemHeight * (window.innerWidth > 1440 ? 10 : 7)
  const listHeight = itemHeight * currentDataLength
  const isDisabled = !sectionEnabled || !currentData

  return (
    <Form.Section.Template
      Icon={ExperimentOutlined}
      title={tTestImpact("title")}
      id={id}
      classes={{ title: !sectionEnabled && styles.disabled }}
    >
      <Form.Item noStyle name={name} />
      {isDisabled && (
        <Flex
          vertical
          gap={16}
          className={cn(styles.wrapper, styles.wrapperDisabled)}
        >
          {!sectionEnabled && (
            <Flex gap={8} align="center">
              <Text type="secondary">
                <LockOutlined />
              </Text>
              <Text type="secondary" className="sm-text-ks">
                {tTestImpact("descriptionNotEnabled")}
              </Text>
            </Flex>
          )}
          {impactCtx.isPending && <LoaderTestImpact />}
        </Flex>
      )}
      <Flex
        vertical
        gap={16}
        className={cn(
          isDisabled && styles.notVisible,
          !sectionEnabled && styles.hide
        )}
      >
        <Flex gap={16} align="center" justify="space-between">
          <Text type="secondary">{tTestImpact("description")}</Text>
        </Flex>
        <Flex flex={1}>
          <StatisticsTestImpact summary={summary} t={tTestImpact} />
        </Flex>
        <Flex vertical className={styles.listWrapper}>
          {currentDataLength === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex vertical align="center">
                  <Text strong type="secondary">
                    {tTestImpact("empty.line1")}
                  </Text>
                  <Text type="secondary" className="sm-text-ks">
                    {tTestImpact("empty.line2")}
                  </Text>
                </Flex>
              }
            />
          )}
          {currentDataLength > 0 && (
            <List
              data={currentData ?? []}
              itemKey="index"
              itemHeight={itemHeight}
              height={listHeight > listMaxHeight ? listMaxHeight : listHeight}
            >
              {(item) => {
                if ("type" in item) {
                  if (item.type === footerType)
                    return (
                      <Flex className={styles.moreResultsButton}>
                        <Button
                          loading={impactCtx.isPending}
                          disabled={impactCtx.isPending || !hasNext}
                          onClick={async () => {
                            try {
                              const variables = impactCtx.variables

                              if (variables && "sessionId" in variables)
                                await impactCtx.mutateAsync({
                                  sessionId: variables?.sessionId,
                                  sampleSize: 20,
                                  showMore: true
                                })
                            } catch (error: any) {
                              message.instance.error(
                                error?.message || "Something went wrong"
                              )
                            }
                          }}
                        >
                          {tTestImpact("moreResults")}
                        </Button>
                      </Flex>
                    )

                  return null
                }

                return <IssueTestImpact item={item} t={tTestImpact} />
              }}
            </List>
          )}
        </Flex>
      </Flex>
    </Form.Section.Template>
  )
}
